import { useEffect, useState } from "react";
import { api, copy, type SavedKey } from "./api";
import { Tokens } from "./Tokens";

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "never");

/** The keys people save most; Other… takes any name. */
const PRESETS = [
  { env: "CLAUDE_CODE_OAUTH_TOKEN", label: "Claude Code token", placeholder: "sk-ant-oat01-…", account: true,
    help: <>Run <code>claude setup-token</code> on a computer where Claude Code is signed in: it opens the browser once and prints a token (sk-ant-oat…) that lasts a year. Uses your Claude subscription.</> },
  { env: "ANTHROPIC_API_KEY", label: "Anthropic API key", placeholder: "sk-ant-api03-…", account: true,
    help: <>From console.anthropic.com › API keys, billed per use. The Claude installer takes it when no Claude Code token is saved.</> },
  { env: "OPENAI_API_KEY", label: "OpenAI API key", placeholder: "sk-…", help: <>For Codex and other OpenAI tools.</> },
  { env: "GEMINI_API_KEY", label: "Gemini API key", placeholder: "AIza…", help: <>From aistudio.google.com, for the Gemini CLI.</> },
  { env: "GITHUB_TOKEN", label: "GitHub token", placeholder: "ghp_… or github_pat_…", help: <>For gh and git over https.</> },
  { env: "", label: "Other…", placeholder: "the value", help: <>Any key, under a name like MY_SERVICE_TOKEN.</> },
];

function CopyLine({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <div className="copyline">
      <code>{text}</code>
      <button type="button" className="btn small" onClick={async () => { if (await copy(text)) { setDone(true); setTimeout(() => setDone(false), 1500); } }}>
        {done ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function Install() {
  return (
    <div className="panel card">
      <h3>Install with one line</h3>
      <ol className="steps">
        <li>Save your <strong>Claude Code token</strong> below (or an Anthropic API key).</li>
        <li>Create an <strong>API key</strong> below; it is shown once, so keep it handy.</li>
        <li>In the new machine's terminal, run:</li>
      </ol>
      <CopyLine text={`curl -fsSL ${location.origin}/install/claude | sh`} />
      <p className="muted small">
        It asks for the API key once (it keeps it in <code>~/.config/mylinux/api-key</code> for the next install), installs
        Claude Code and starts it signed in: no login screens. It runs on Debian, Alpine, Omarchy and macOS.
      </p>
    </div>
  );
}

function SavedKeys() {
  const [list, setList] = useState<SavedKey[] | null>(null);
  const [error, setError] = useState("");
  const [preset, setPreset] = useState(0);
  const [env, setEnv] = useState(PRESETS[0].env);
  const [label, setLabel] = useState(PRESETS[0].account ? "" : PRESETS[0].label);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [renaming, setRenaming] = useState("");
  const [newName, setNewName] = useState("");

  const load = () => api<SavedKey[]>("/api/keys").then(setList).catch((e) => { setList([]); setError(e.message); });
  useEffect(() => { load(); }, []);

  function choose(i: number, keepEnv?: string, keepLabel?: string) {
    setPreset(i);
    setEnv(keepEnv ?? PRESETS[i].env);
    // a Claude key's name is the subscription's (viktor_gmail): asked for, not the kind's name
    setLabel(keepLabel ?? (PRESETS[i].env && !PRESETS[i].account ? PRESETS[i].label : ""));
    setValue("");
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api(`/api/keys/${encodeURIComponent(env.trim())}`, { method: "PUT", body: JSON.stringify({ label: label.trim() || PRESETS[preset].label || env.trim(), value }) });
      setValue("");
      await load();
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }

  async function copyValue(k: SavedKey) {
    setError("");
    try {
      const v = await api<string>(`/api/keys/${k.env}/value`);
      if (await copy(v)) { setCopied(k.env); setTimeout(() => setCopied(""), 1500); }
      else setError("The browser did not allow copying here.");
      load();
    } catch (err) { setError((err as Error).message); }
  }

  async function remove(k: SavedKey) {
    if (!confirm(`Delete "${k.label}"? Installers can no longer fetch it.`)) return;
    await api(`/api/keys/${k.env}`, { method: "DELETE" });
    load();
  }

  // inline, not window.prompt: embedded browsers (Electron, in-app ones) return null from prompt without showing it
  function startRename(k: SavedKey) {
    const p = PRESETS.find((x) => x.env === k.env);
    setRenaming(k.env);
    setNewName(k.label === p?.label ? "" : k.label);
  }

  async function rename(e: React.FormEvent, k: SavedKey) {
    e.preventDefault();
    if (!newName.trim()) return;
    setError("");
    try {
      await api(`/api/keys/${encodeURIComponent(k.env)}`, { method: "PUT", body: JSON.stringify({ label: newName.trim() }) });
      setRenaming("");
      load();
    } catch (err) { setError((err as Error).message); }
  }

  function replace(k: SavedKey) {
    const i = PRESETS.findIndex((p) => p.env === k.env);
    choose(i >= 0 ? i : PRESETS.length - 1, k.env, k.label);
    document.getElementById("key-value")?.focus();
  }

  const p = PRESETS[preset];
  return (
    <>
      <p className="muted">
        Keys your machines' installers fetch with an API key. They are kept encrypted, and this page never shows them;
        Copy puts one on the clipboard.
      </p>
      {list === null ? <div className="empty">Loading…</div> : list.length > 0 && (
        <div className="list" style={{ marginBottom: 14 }}>
          {list.map((k) => (
            <div className="panel" key={k.id}>
              <div className="grow">
                <div className="name">{k.label} <span className="mono muted" style={{ fontWeight: 400 }}>{k.hint ? `…${k.hint}` : ""}</span></div>
                <div className="meta"><span className="mono">{k.env}</span> · saved {when(k.updated_at)} · last read {when(k.last_read_at)}</div>
              </div>
              {renaming === k.env ? (
                <form className="row actions" onSubmit={(e) => rename(e, k)}>
                  <input type="text" className="grow" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                    placeholder={PRESETS.find((x) => x.env === k.env)?.account ? "Subscription name, e.g. viktor_gmail" : "New name"}
                    aria-label="New name" onKeyDown={(e) => { if (e.key === "Escape") setRenaming(""); }} />
                  <button className="btn small primary" disabled={!newName.trim()}>Save</button>
                  <button type="button" className="btn small" onClick={() => setRenaming("")}>Cancel</button>
                </form>
              ) : (
                <div className="row actions">
                  <button className="btn small" onClick={() => copyValue(k)}>{copied === k.env ? "Copied" : "Copy"}</button>
                  <button className="btn small" onClick={() => startRename(k)}>Rename</button>
                  <button className="btn small" onClick={() => replace(k)}>Replace</button>
                  <button className="btn small danger" onClick={() => remove(k)}>Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <form className="panel card keyform" onSubmit={save}>
        <div className="row">
          <select value={preset} onChange={(e) => choose(Number(e.target.value))} aria-label="Kind of key">
            {PRESETS.map((x, i) => <option key={i} value={i}>{x.label}</option>)}
          </select>
          {p.account && (
            <input type="text" className="grow" placeholder="Subscription name, e.g. viktor_gmail" value={label}
              onChange={(e) => setLabel(e.target.value)} aria-label="Subscription name" />
          )}
          {!p.env && (
            <>
              <input type="text" className="mono" placeholder="NAME_LIKE_THIS" value={env} required pattern="[A-Z][A-Z0-9_]{1,63}"
                onChange={(e) => setEnv(e.target.value.toUpperCase())} aria-label="Name" />
              <input type="text" placeholder="What it is" value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Label" />
            </>
          )}
        </div>
        <p className="muted small">{p.help}{p.account && " The subscription's name goes to the machine as MYLINUX_CLAUDE_ACCOUNT, which the status line shows."}</p>
        <div className="row">
          <input id="key-value" type="password" className="mono grow" placeholder={p.placeholder} value={value} required
            autoComplete="off" spellCheck={false} onChange={(e) => setValue(e.target.value)} aria-label="Value" />
          <button className="btn primary" disabled={busy || !value.trim() || !env.trim()}>
            {list?.some((k) => k.env === env.trim()) ? "Replace" : "Save"}
          </button>
        </div>
      </form>
      {error && <div className="notice error" style={{ marginBottom: 16 }}>{error}</div>}
    </>
  );
}

export function Api() {
  return (
    <>
      <Install />
      <h2 className="section">Saved keys</h2>
      <SavedKeys />
      <h2 className="section">API keys</h2>
      <Tokens />
    </>
  );
}
